import { useCallback, useState } from "react";
import styled from "@emotion/styled";
import { Input } from "antd5";
import isEqual from "lodash/isEqual";

import { DetailsContent } from "components/app_layout/DetailsLayout";
import { EmptyBidResponses } from "components/bid_module/bid-response/EmptyBidResponses";
import { UserDropdownFilter } from "components/form_components/dropdown_list_selects/UserDropdownFilter";
import Button from "components/ui/button/Button";
import EmptyState from "components/ui/empty_state/EmptyState";
import Badge from "lib/core_components/Badge";
import { useDebouncedValue } from "lib/debounce";
import { useBidResponses } from "lib/hooks/api/bid/bid_responses/useBidResponses";
import { useCustomerDocuments } from "lib/hooks/api/customer_documents/useCustomerDocuments";
import { useLocalStorage } from "lib/hooks/useLocalStorage";
import { parsePaginationUrlState, useURLState } from "lib/hooks/useURLState";
import UIcon from "lib/icons/untitled_ui/UIcon";
import {
  CustomerDocumentSortBy,
  CustomerDocumentSortOrder,
  CustomerDocumentTypeFilter,
} from "lib/types/graphQLEnums";
import { simpleArrayDedupe } from "lib/utils";
import { useTablePageSize } from "../../../lib/hooks/useTablePageSize";
import { Flex, Text } from "../../../styles/utility-components";
import { CustomerDocumentDrawer } from "../../customer_documents/CustomerDocumentDrawer";
import { BidResponseDrawer } from "../bid_response_drawer/BidResponseDrawer";
import { AnswerSource } from "../types";
import { BidResponseStageButton } from "./bid_response_stage/BidResponseStageButton";
import BidResponseActionBar from "./BidResponseActionBar";
import { useBidResponseProcessingContext } from "./BidResponsesProcessingProvider";
import BidResponsesTable from "./BidResponsesTable";
import { ExportBidResponsesButton } from "./ExportBidResponsesButton";
import { BidResponsesFilters, BidResponsesFiltersSchema, BidResponseSortState } from "./utils";

type Props = {
  defaultFilters: BidResponsesFilters;
  bidId: string | undefined;
  bidName?: string;
};

export default function FilterableBidResponses({
  bidId: bidIdProp,
  defaultFilters,
  bidName,
}: Props) {
  const [bidResponseWarning, setBidResponseWarning] = useLocalStorage("bidResponseWarning", true);

  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);

  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [selectedSource, setSelectedSource] = useState<AnswerSource | null>(null);

  const [filters = defaultFilters, setBidResponseFilters] = useURLState(
    "bidResponseFilters",
    defaultFilters,
    BidResponsesFiltersSchema,
    { encoder: "compress" },
  );

  const bidId = filters.bidId ?? bidIdProp ?? undefined;

  const [pageSize, setPageSize] = useTablePageSize("bid-responses-table");
  const [pagination = { page: 1, pageSize }, setPagination] = useURLState(
    "pagination",
    { page: 1, pageSize },
    (state) => parsePaginationUrlState(state, { page: 1, pageSize }),
  );

  const [debouncedFilters] = useDebouncedValue(filters, 500);
  const [debouncedPagination, updateDebouncedPagination] = useDebouncedValue(pagination, 500);

  const setFilters = useCallback(
    (val: BidResponsesFilters) => {
      setBidResponseFilters(val);
      setPagination({ page: 1, pageSize });
    },
    [setBidResponseFilters, setPagination, pageSize],
  );
  const handleSortChange = useCallback(
    (newSort: BidResponseSortState) => {
      setFilters({
        ...filters,
        orderBy: newSort.field,
        order: newSort.order,
      });
    },
    [filters, setFilters],
  );

  const {
    data: bidResponses,
    totalCount,
    isLoading,
    isError,
  } = useBidResponses(debouncedFilters, debouncedPagination);

  const { data: documents, isLoading: isLoadingDocuments } = useCustomerDocuments(
    {
      resourceId: bidId,
      sortBy: CustomerDocumentSortBy.FileName,
      sortOrder: CustomerDocumentSortOrder.Asc,
      documentType: CustomerDocumentTypeFilter.BidDocument,
    },
    {
      page: 1,
      pageSize: 500,
    },
    { enabled: !!bidId },
  );

  const { status, isExtracting } = useBidResponseProcessingContext(bidId);

  const noBidResponses =
    !isLoading &&
    !isError &&
    isEqual(debouncedFilters, defaultFilters) &&
    bidResponses?.length === 0;

  // The extraction-led empty state only makes sense inside a single bid, since it offers
  // to pull questions out of that bid's tender documents.
  if (bidId && (isExtracting || noBidResponses)) {
    return (
      <Content>
        <EmptyBidResponses
          bidId={bidId}
          isExtracting={isExtracting}
          isLoadingDocuments={isLoadingDocuments}
          status={status}
          documents={documents}
        />
      </Content>
    );
  }

  if (!bidId && noBidResponses) {
    return (
      <Content>
        <EmptyState
          type="noResults"
          heading="No questions yet"
          description="Questions from every bid your team is working on will appear here once they have been added."
          size="large"
          width="450px"
        />
      </Content>
    );
  }

  const hasBidResponses = bidResponses && bidResponses.length > 0;
  return (
    <Content>
      {bidId && hasBidResponses && bidResponseWarning && (
        <WarningBanner>
          <Flex gap={12} alignItems="center">
            <UIcon icon="alertTriangle" color="sysWarningDefault" size={20} />
            <Text>
              This AI-generated extraction may be incomplete or inaccurate. Please verify all
              content against the original tender documents.
            </Text>
          </Flex>
          <Button
            icon="xClose"
            buttonStyle="inverse"
            buttonType="tertiary"
            iconProps={{ color: "sysTextDefault" }}
            onClick={() => setBidResponseWarning(false)}
          />
        </WarningBanner>
      )}
      <Header>
        <Text h1>Bid responses</Text>
        {/* Creating and exporting both need a single bid to act on. */}
        {bidId && hasBidResponses && (
          <Flex gap={8}>
            <Button onClick={() => setIsCreateDrawerOpen(true)} buttonType="secondary">
              Add response
            </Button>
            <ExportBidResponsesButton
              filters={{
                ...filters,
                ids: selectedKeys.length > 0 ? selectedKeys : filters.ids,
              }}
              totalCount={selectedKeys.length > 0 ? selectedKeys.length : totalCount}
              bidName={bidName}
            />
          </Flex>
        )}
      </Header>
      <Flex gap={8}>
        <InputContainer>
          <Input
            placeholder="Search questions"
            value={filters.textSearch ?? ""}
            onChange={(e) => {
              setFilters({ ...filters, textSearch: e.target.value });
            }}
            prefix={<UIcon icon="searchSm" color="sysPrimaryDefault" size={16} />}
          />
        </InputContainer>
        <BidResponseStageButton
          buttonType="secondary"
          chevron
          loading={isLoading}
          selectedIds={filters.stageIds}
          showCheckBox
          buttonContent={
            <Flex alignItems="center">
              <Text>All statuses</Text>
              <Badge hide={!filters.stageIds?.length}>{filters.stageIds?.length}</Badge>
            </Flex>
          }
          onClick={(stageId) => {
            let stageIds = filters.stageIds ?? [];

            if (stageIds.includes(stageId)) {
              stageIds = stageIds.filter((s) => s !== stageId);
            } else {
              stageIds = simpleArrayDedupe([...(filters.stageIds || []), stageId]);
            }

            setFilters({
              ...filters,
              stageIds: stageIds.length === 0 ? undefined : stageIds,
            });
          }}
        />
        {/* Across bids the table is a task list, so narrowing to one person is the filter
            that makes it usable. Within a bid the assignee column is enough.
            The unassigned option is hidden because the query filters on assignee ids only,
            so there is nothing to send for it. */}
        {!bidId && (
          <UserDropdownFilter
            value={{ ids: filters.assigneeIds ?? [] }}
            onChange={({ ids }) =>
              setFilters({ ...filters, assigneeIds: ids.length > 0 ? ids : undefined })
            }
            options={{ noun: "assignee", hideUnassignedOption: true }}
          />
        )}
      </Flex>
      <TableContainer>
        {selectedKeys.length > 0 && (
          <BidResponseActionBar
            selectedIds={selectedKeys}
            clearSelection={() => setSelectedKeys([])}
            filters={filters}
            bidName={bidName}
          />
        )}
        <BidResponsesTable
          bidResponses={bidResponses}
          isLoading={isLoading}
          isError={isError}
          bidId={bidId}
          selectedKeys={selectedKeys}
          filters={filters}
          onSortChange={handleSortChange}
          pagination={{
            current: pagination.page ?? 1,
            pageSize: pagination.pageSize ?? 20,
            total: totalCount,
            onChange: (page, pageSize) => {
              setPageSize(pageSize);
              setPagination({ page, pageSize });
              updateDebouncedPagination({ page, pageSize });
            },
            showSizeChanger: true,
            align: "center",
          }}
          setSelectedKeys={setSelectedKeys}
          setSelectedSource={setSelectedSource}
        />
      </TableContainer>
      <BidResponseDrawer
        type="create"
        isOpen={isCreateDrawerOpen}
        onClose={() => setIsCreateDrawerOpen(false)}
        onComplete={() => setIsCreateDrawerOpen(false)}
        defaultValues={{
          bidId,
        }}
      />
      {selectedSource && (
        <CustomerDocumentDrawer
          source={selectedSource}
          onClose={() => setSelectedSource(null)}
          isOpen={!!selectedSource}
        />
      )}
    </Content>
  );
}

const Content = styled(DetailsContent)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: 32,
});

const WarningBanner = styled(Flex)(({ theme }) => ({
  backgroundColor: theme.colors.sysWarningSubtle,
  border: `1px solid ${theme.colors.sysWarningDefault}`,
  borderRadius: 8,
  padding: "16px 20px",
  alignItems: "center",
  alignSelf: "stretch",
  justifyContent: "space-between",
}));

const Header = styled(Flex)({
  justifyContent: "space-between",
  alignItems: "center",
});

const TableContainer = styled(Flex)({
  flexDirection: "column",
  gap: 12,
  ".ant5-table-cell": {
    verticalAlign: "top",
    padding: "24px !important",
  },
});

const InputContainer = styled.div({
  width: 300,
});
